import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import {
  uploadProfilePhoto,
  updateUserProfile,
  getUserProfile,
  getUserBadges,
  updateBadgeOrder,
  addExternalLink,
  removeExternalLink
} from '../../../services/profileService';

const ProfileSection = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [badges, setBadges] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [editedBio, setEditedBio] = useState('');
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [showAllBadges, setShowAllBadges] = useState(false);
  const [draggedBadgeId, setDraggedBadgeId] = useState(null);

  const MAX_VISIBLE_BADGES = 4;

  useEffect(() => {
    if (user?.id) {
      loadProfile();
      loadBadges();
    }
  }, [user?.id]);

  const loadProfile = async () => {
    try {
      const data = await getUserProfile(user?.id);
      setProfile(data);
      setEditedBio(data?.bio || '');
    } catch (error) {
      console.error('Failed to load profile:', error);
    }
  };

  const loadBadges = async () => {
    try {
      const data = await getUserBadges(user?.id);
      setBadges(data || []);
    } catch (error) {
      console.error('Failed to load badges:', error);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file || !user?.id) return;

    // Validate file size (2MB)
    if (file?.size > 2097152) {
      alert('File size must be less than 2MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes?.includes(file?.type)) {
      alert('Only JPEG, PNG, and WebP images are allowed');
      return;
    }

    setIsUploading(true);
    try {
      const filePath = await uploadProfilePhoto(user?.id, file);
      await updateUserProfile(user?.id, { profile_photo_url: filePath });
      await loadProfile();
    } catch (error) {
      console.error('Failed to upload photo:', error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveBio = async () => {
    if (!user?.id) return;

    try {
      await updateUserProfile(user?.id, { bio: editedBio });
      await loadProfile();
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save bio:', error);
      alert('Failed to save bio. Please try again.');
    }
  };

  const handleAddLink = async () => {
    if (!user?.id || !newLinkTitle || !newLinkUrl) return;

    try {
      await addExternalLink(user?.id, {
        title: newLinkTitle,
        url: newLinkUrl
      });
      setNewLinkTitle('');
      setNewLinkUrl('');
      await loadProfile();
    } catch (error) {
      console.error('Failed to add link:', error);
      alert('Failed to add link. Please try again.');
    }
  };

  const handleRemoveLink = async (index) => {
    if (!user?.id) return;

    try {
      await removeExternalLink(user?.id, index);
      await loadProfile();
    } catch (error) {
      console.error('Failed to remove link:', error);
      alert('Failed to remove link. Please try again.');
    }
  };

  const handleBadgeDragStart = (badgeId) => {
    setDraggedBadgeId(badgeId);
  };

  const handleBadgeDrop = async (targetBadgeId) => {
    if (!draggedBadgeId || draggedBadgeId === targetBadgeId) return;

    const draggedIndex = badges?.findIndex(b => b?.id === draggedBadgeId);
    const targetIndex = badges?.findIndex(b => b?.id === targetBadgeId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Reorder badges
    const newBadges = [...badges];
    const [removed] = newBadges?.splice(draggedIndex, 1);
    newBadges?.splice(targetIndex, 0, removed);

    // Update display order
    const badgeOrders = newBadges?.map((badge, index) => ({
      id: badge?.id,
      display_order: index
    }));

    setBadges(newBadges);
    setDraggedBadgeId(null);

    try {
      await updateBadgeOrder(user?.id, badgeOrders);
    } catch (error) {
      console.error('Failed to update badge order:', error);
      await loadBadges(); // Reload on error
    }
  };

  const displayedBadges = showAllBadges ? badges : badges?.slice(0, MAX_VISIBLE_BADGES);
  const hasMoreBadges = badges?.length > MAX_VISIBLE_BADGES;
  const externalLinks = profile?.external_links || [];

  if (!user) return null;

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Photo Section */}
        <div className="flex flex-col items-center space-y-4">
          <div className="relative group">
            {profile?.photoUrl ? (
              <img
                src={profile?.photoUrl}
                alt={profile?.display_name || 'User'}
                className="w-32 h-32 rounded-full object-cover border-4 border-primary"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-muted flex items-center justify-center border-4 border-primary">
                <Icon name="User" size={48} className="text-muted-foreground" />
              </div>
            )}
            <label
              htmlFor="photo-upload"
              className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            >
              <Icon name={isUploading ? "Loader2" : "Camera"} size={24} className={`text-white ${isUploading ? 'animate-spin' : ''}`} />
              <input
                id="photo-upload"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handlePhotoUpload}
                disabled={isUploading}
              />
            </label>
          </div>
          <h3 className="text-lg font-semibold text-card-foreground">
            {profile?.display_name || profile?.full_name || 'User'}
          </h3>
        </div>

        {/* Bio and Links Section */}
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-card-foreground">Bio</h4>
              {!isEditing && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  iconName="Edit"
                />
              )}
            </div>
            {isEditing ? (
              <div className="space-y-2">
                <textarea
                  value={editedBio}
                  onChange={(e) => setEditedBio(e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground resize-none"
                  rows={4}
                  placeholder="Write a brief bio about yourself..."
                  maxLength={500}
                />
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsEditing(false);
                      setEditedBio(profile?.bio || '');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleSaveBio}
                  >
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {profile?.bio || 'Add a bio to tell others about yourself'}
              </p>
            )}
          </div>

          <div>
            <h4 className="font-semibold text-card-foreground mb-2">Links</h4>
            {externalLinks?.length > 0 && (
              <div className="space-y-2 mb-3">
                {externalLinks?.map((link, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-md">
                    <a
                      href={link?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 text-sm text-primary hover:underline flex-1"
                    >
                      <Icon name="Link" size={14} />
                      <span className="truncate">{link?.title}</span>
                    </a>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveLink(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Icon name="X" size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2">
              <Input
                type="text"
                placeholder="Link title"
                value={newLinkTitle}
                onChange={(e) => setNewLinkTitle(e?.target?.value)}
                className="text-sm"
              />
              <div className="flex space-x-2">
                <Input
                  type="url"
                  placeholder="https://example.com"
                  value={newLinkUrl}
                  onChange={(e) => setNewLinkUrl(e?.target?.value)}
                  className="text-sm flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddLink}
                  disabled={!newLinkTitle || !newLinkUrl}
                  iconName="Plus"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Badges Section */}
        <div>
          <h4 className="font-semibold text-card-foreground mb-3">Badges Earned</h4>
          {badges?.length > 0 ? (
            <div>
              <div className="grid grid-cols-2 gap-3">
                {displayedBadges?.map((userBadge) => (
                  <div
                    key={userBadge?.id}
                    draggable
                    onDragStart={() => handleBadgeDragStart(userBadge?.id)}
                    onDragOver={(e) => e?.preventDefault()}
                    onDrop={() => handleBadgeDrop(userBadge?.id)}
                    className="flex flex-col items-center p-3 bg-muted rounded-lg cursor-move hover:bg-muted/80 transition-colors"
                    title={userBadge?.badge?.description}
                  >
                    {userBadge?.badge?.icon_url ? (
                      <img
                        src={userBadge?.badge?.icon_url}
                        alt={userBadge?.badge?.name}
                        className="w-12 h-12 mb-2"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mb-2">
                        <Icon name="Award" size={24} className="text-primary" />
                      </div>
                    )}
                    <p className="text-xs font-medium text-center text-card-foreground">
                      {userBadge?.badge?.name}
                    </p>
                  </div>
                ))}
              </div>
              {hasMoreBadges && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAllBadges(!showAllBadges)}
                  className="w-full mt-3"
                  iconName={showAllBadges ? "ChevronUp" : "ChevronDown"}
                  iconPosition="right"
                >
                  {showAllBadges ? 'Show Less' : `See More (${badges?.length - MAX_VISIBLE_BADGES})`}
                </Button>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <Icon name="Award" size={32} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                No badges earned yet. Keep donating to unlock achievements!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileSection;